import type { ComponentPropsWithRef, ReactNode } from 'react';
import React from 'react';

import clsx from 'clsx';
import { CheckboxCheckIcon } from 'src/components/icons/CheckboxCheckIcon';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  box: {
    display: 'flex',
    flexShrink: 0,
    position: 'relative',
  },
  checkIcon: {
    display: 'none',
    left: 3,
    pointerEvents: 'none',
    position: 'absolute',
    top: 3,
  },
  disabled: { opacity: 0.5 },
  input: {
    '&:checked': {
      backgroundColor: theme.appColors.primary,
      borderColor: theme.appColors.primary,
    },
    '&:checked + svg': { display: 'block' },
    '&:disabled': { cursor: 'default' },
    '&:focus-visible': {
      outline: `2px solid ${theme.appColors.halfPrimary}`,
      outlineOffset: 1,
    },
    appearance: 'none',
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 4,
    boxSizing: 'border-box',
    cursor: 'pointer',
    height: 16,
    margin: 0,
    width: 16,
  },
  label: {
    color: theme.appColors.iconsType,
    fontSize: 12,
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

export interface SmallCheckboxProps extends Omit<
  ComponentPropsWithRef<'input'>,
  'className' | 'type'
> {
  /** Applied to the root, not the input. */
  className?: string;
  /** Clickable text beside the box. Without it, pass an aria-label. */
  label?: ReactNode;
}

export function SmallCheckbox({
  className,
  disabled,
  label,
  ...inputProps
}: SmallCheckboxProps): JSX.Element {
  const { classes } = useStyles();
  const Root = label ? 'label' : 'span';

  return (
    <Root className={clsx(classes.root, disabled && classes.disabled, className)}>
      <span className={classes.box}>
        <input className={classes.input} disabled={disabled} type="checkbox" {...inputProps} />
        <CheckboxCheckIcon className={classes.checkIcon} />
      </span>
      {Boolean(label) && <span className={classes.label}>{label}</span>}
    </Root>
  );
}
